import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  withArrow?: boolean;
  fullWidth?: boolean;
  href?: string;
  trackAddToCart?: boolean;
}
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  withArrow = false,
  fullWidth = false,
  href,
  trackAddToCart = false,
  className = '',
  onClick,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-300 rounded-none tracking-wide';
  const variants = {
    primary: 'bg-[#D4AF37] text-white hover:bg-[#B4941F] shadow-lg hover:shadow-xl',
    secondary: 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg',
    outline: 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white',
    text: 'text-black hover:text-[#D4AF37] underline-offset-4 hover:underline p-0'
  };
  const sizes = {
    sm: 'text-sm px-4 py-2',
    md: 'text-base px-6 py-3',
    lg: 'text-lg px-8 py-4'
  };
  const combinedClassName = `
    ${baseStyles} 
    ${variants[variant]} 
    ${variant !== 'text' ? sizes[size] : ''} 
    ${fullWidth ? 'w-full' : ''} 
    ${className}
  `;
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    console.log('Button clicked!', {
      trackAddToCart,
      href
    });
    if (trackAddToCart) {
      if (typeof window === 'undefined') {
        console.warn('Window is undefined');
        return;
      }
      if (!(window as any).fbq) {
        console.warn('Meta Pixel (fbq) not loaded yet');
        return;
      }
      // Prevent immediate navigation for links
      if (href) {
        e.preventDefault();
        console.log('Prevented default navigation');
        // Fire the pixel event with explicit pixel ID
        console.log('Firing Meta Pixel AddToCart event for pixel 752017830664443');
        try {
          ;
          (window as any).fbq('trackSingle', '752017830664443', 'AddToCart');
          console.log('AddToCart event fired successfully');
        } catch (error) {
          console.error('Error firing AddToCart event:', error);
        }
        // Navigate after a longer delay to ensure pixel fires and sends data
        setTimeout(() => {
          console.log('Navigating to:', href);
          window.location.href = href;
        }, 500);
      } else {
        // For buttons without href, just fire the event
        console.log('Firing Meta Pixel AddToCart event for pixel 752017830664443 (no href)');
        try {
          ;
          (window as any).fbq('trackSingle', '752017830664443', 'AddToCart');
          console.log('AddToCart event fired successfully');
        } catch (error) {
          console.error('Error firing AddToCart event:', error);
        }
      }
    }
    // Call original onClick if provided and no href
    if (onClick && !href) {
      onClick(e as any);
    }
  };
  const content = <>
      {children}
      {withArrow && <ArrowRight className="ml-2 h-4 w-4" data-id="element-288" />}
    </>;
  if (href) {
    return <motion.a href={href} onClick={handleClick} whileHover={variant !== 'text' ? {
      scale: 1.02
    } : {}} whileTap={variant !== 'text' ? {
      scale: 0.98
    } : {}} className={combinedClassName} data-id="element-289">
        {content}
      </motion.a>;
  }
  return <motion.button onClick={handleClick} whileHover={variant !== 'text' ? {
    scale: 1.02
  } : {}} whileTap={variant !== 'text' ? {
    scale: 0.98
  } : {}} className={combinedClassName} {...props} data-id="element-290">
      {content}
    </motion.button>;
}